import { format, subDays } from "date-fns"
import {
  Activity,
  Award,
  BarChart3,
  CheckCircle,
  CreditCard,
  DollarSign,
  GitBranch,
  PieChart as PieChartIcon,
  RefreshCw,
} from "lucide-react"
import CountUp from "react-countup"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { DateRangePicker } from "@/components/dashboard/date-range-picker"
import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { DashboardContainer } from "@/components/layout/dashboard-container"
import { Header } from "@/components/layout/header"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { FRONTEND_ROUTES } from "@/constants"
import { useSmartRoutingDashboardMetrics } from "@/hooks"

const COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#00C49F",
  "#FFBB28",
]

const EmptyState = ({
  messageKey,
}: {
  messageKey?: string
}) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-3 text-5xl opacity-10">📊</div>
      <p className="text-sm font-medium text-muted-foreground">
        {messageKey
          ? t(messageKey)
          : t("common.noDataAvailable")}
      </p>
      <p className="mt-1 text-xs text-muted-foreground/60">
        {t("common.tryAdjustingFilters")}
      </p>
    </div>
  )
}

const MetricCard = ({
  title,
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
  isLoading,
  icon: Icon,
  iconColor = "text-primary",
  iconBgColor = "bg-primary/10",
  borderColor = "border-t-primary",
}: {
  title: string
  value: number
  prefix?: string
  suffix?: string
  decimals?: number
  isLoading: boolean
  icon: React.ElementType
  iconColor?: string
  iconBgColor?: string
  borderColor?: string
}) => {
  const hasData =
    value !== null && value !== undefined && value >= 0

  return (
    <Card
      className={`group relative overflow-hidden border-t-4 ${borderColor} transition-all duration-300 hover:shadow-lg`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      <CardHeader className="relative pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          <div
            className={`rounded-full ${iconBgColor} p-2`}
          >
            <Icon className={`h-4 w-4 ${iconColor}`} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative">
        {isLoading ? (
          <Skeleton className="h-10 w-32" />
        ) : !hasData ? (
          <div className="text-sm text-muted-foreground">
            Sem dados disponíveis
          </div>
        ) : (
          <div className="flex items-baseline gap-2">
            <CountUp
              start={0}
              end={value}
              separator="."
              decimals={decimals}
              decimal=","
              prefix={prefix}
              suffix={suffix}
              className="text-2xl font-bold @sm:text-3xl"
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function SmartRoutingDashboardPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const searchParamsObject = Object.fromEntries(
    searchParams.entries()
  )

  // Datas dos parâmetros de URL ou padrão (últimos 3 dias)
  const endDate = searchParamsObject.endDate
    ? new Date(searchParamsObject.endDate)
    : new Date()
  const startDate = searchParamsObject.startDate
    ? new Date(searchParamsObject.startDate)
    : subDays(new Date(), 2)

  // Buscar dados da API
  const { data, isLoading, error } =
    useSmartRoutingDashboardMetrics({
      startDate,
      endDate,
      enabled: true,
    })

  return (
    <DashboardContainer>
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.SMART_ROUTING.href,
              label: t("sidebar.smartRouting"),
            },
            {
              label: t(
                "pages.smartRouting.dashboard.metricsTitle"
              ),
            },
          ]}
        />
      </Header>
      <section className="flex flex-1 flex-col gap-4 overflow-x-hidden p-4 @container">
        <div className="flex flex-col gap-4 @md:flex-row @md:items-center @md:justify-between">
          <h1 className="text-xl font-bold @md:text-2xl">
            {t("pages.smartRouting.dashboard.metricsTitle")}
          </h1>
          <DateRangePicker
            searchParams={searchParamsObject}
          />
        </div>

        {/* Estados de loading e erro */}
        {isLoading && (
          <div className="space-y-6">
            {/* Skeleton Métricas */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-96" />
              </div>
              <div className="grid gap-4 @sm:grid-cols-2 @2xl:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-10 w-10 rounded-full" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-10 w-24" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Skeleton Gráficos */}
            <div className="grid grid-cols-1 gap-6 @xl:grid-cols-2">
              {[1, 2].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <Skeleton className="h-6 w-48" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-[250px] w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Skeleton Performance */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <Skeleton className="h-6 w-56" />
                </div>
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[300px] w-full" />
              </CardContent>
            </Card>

            {/* Skeleton Grid Lateral */}
            <div className="grid grid-cols-1 gap-6 @xl:grid-cols-2">
              {[1, 2].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <Skeleton className="h-6 w-40" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[1, 2, 3].map((j) => (
                        <Skeleton
                          key={j}
                          className="h-24 w-full"
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="mb-2 text-xl font-bold text-red-800">
              {t("pages.smartRouting.dashboard.errorTitle")}
            </h2>
            <p className="text-red-600">
              {t("pages.smartRouting.dashboard.error")}
            </p>
          </div>
        )}

        {/* Dados do dashboard */}
        {!isLoading && (
          <div className="space-y-6">
            {/* Métricas Principais */}
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">
                  {t(
                    "pages.smartRouting.dashboard.metrics.title"
                  )}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "pages.smartRouting.dashboard.description"
                  )}
                </p>
              </div>

              <div className="grid gap-4 @sm:grid-cols-2 @2xl:grid-cols-4">
                <MetricCard
                  title={t(
                    "pages.smartRouting.dashboard.metrics.totalTransactions"
                  )}
                  value={data?.totalTransactions ?? 0}
                  isLoading={isLoading}
                  icon={Activity}
                  iconColor="text-blue-600"
                  iconBgColor="bg-blue-50"
                  borderColor="border-t-blue-500"
                />
                <MetricCard
                  title={t(
                    "pages.smartRouting.dashboard.metrics.approvalRate"
                  )}
                  value={data?.approvalRate ?? 0}
                  suffix="%"
                  decimals={1}
                  isLoading={isLoading}
                  icon={CheckCircle}
                  iconColor="text-green-600"
                  iconBgColor="bg-green-50"
                  borderColor="border-t-green-500"
                />
                <MetricCard
                  title={t(
                    "pages.smartRouting.dashboard.metrics.failoverRate"
                  )}
                  value={data?.failoverRate ?? 0}
                  suffix="%"
                  decimals={1}
                  isLoading={isLoading}
                  icon={RefreshCw}
                  iconColor="text-orange-600"
                  iconBgColor="bg-orange-50"
                  borderColor="border-t-orange-500"
                />
                <MetricCard
                  title={t(
                    "pages.smartRouting.dashboard.metrics.totalVolume"
                  )}
                  value={data?.totalVolume ?? 0}
                  prefix="R$ "
                  decimals={2}
                  isLoading={isLoading}
                  icon={DollarSign}
                  iconColor="text-purple-600"
                  iconBgColor="bg-purple-50"
                  borderColor="border-t-purple-500"
                />
              </div>
            </div>

            {/* Gráficos */}
            <div className="grid grid-cols-1 gap-6 @xl:grid-cols-2">
              {/* Gráfico de Transações Roteadas */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="rounded-full bg-primary/10 p-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">
                      Transações Roteadas
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {(
                    data?.routedTransactions?.dataPoints ??
                    []
                  ).length === 0 ? (
                    <EmptyState />
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height={300}
                    >
                      <LineChart
                        data={
                          data?.routedTransactions
                            ?.dataPoints ?? []
                        }
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) =>
                            format(new Date(value), "dd/MM")
                          }
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(value) =>
                            `Data: ${format(new Date(value), "dd/MM/yyyy")}`
                          }
                          formatter={(value) => [
                            value,
                            "Transações",
                          ]}
                        />
                        <Line
                          type="monotone"
                          dataKey="transactionCount"
                          stroke="#8884d8"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Gráfico de Distribuição de Processadores */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="rounded-full bg-primary/10 p-2">
                      <PieChartIcon className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">
                      Distribuição de Processadores
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Gráfico de Pizza */}
                    <ResponsiveContainer
                      width="100%"
                      height={250}
                    >
                      <PieChart>
                        <Pie
                          data={
                            data?.processorDistribution ??
                            []
                          }
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="percentage"
                          nameKey="processor"
                        >
                          {(
                            data?.processorDistribution ??
                            []
                          ).map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                COLORS[
                                  index % COLORS.length
                                ]
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [
                            `${Number(value).toFixed(1)}%`,
                            name,
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>

                    {/* Legenda Customizada */}
                    {(data?.processorDistribution ?? [])
                      .length === 0 ? (
                      <EmptyState />
                    ) : (
                      <div className="grid grid-cols-1 gap-2 @sm:grid-cols-2">
                        {(
                          data?.processorDistribution ?? []
                        ).map((item, index) => (
                          <div
                            key={item.processor}
                            className="flex items-center justify-between rounded-lg border p-3"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="h-3 w-3 rounded-full"
                                style={{
                                  backgroundColor:
                                    COLORS[
                                      index % COLORS.length
                                    ],
                                }}
                              />
                              <span className="text-sm font-medium">
                                {item.processor}
                              </span>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-primary">
                                {item.percentage.toFixed(1)}
                                %
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {item.totalTransactions} txs
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Performance de Processadores */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-primary/10 p-2">
                    <GitBranch className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">
                    Performance de Processadores
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {(data?.processorPerformance ?? [])
                  .length === 0 ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer
                    width="100%"
                    height={300}
                  >
                    <BarChart
                      data={
                        data?.processorPerformance ?? []
                      }
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="processor" />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="totalAsPrimary"
                        fill="#8884d8"
                        name="Total como Primário"
                      />
                      <Bar
                        dataKey="successOnPrimary"
                        fill="#82ca9d"
                        name="Sucessos Primário"
                      />
                      <Bar
                        dataKey="failedOnPrimary"
                        fill="#ff7300"
                        name="Falhas Primário"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Grid para Métricas por Razão e Sucesso dos Processadores */}
            <div className="grid grid-cols-1 gap-6 @xl:grid-cols-2">
              {/* Métricas por Razão */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="rounded-full bg-primary/10 p-2">
                      <RefreshCw className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">
                      Métricas por Razão
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {(data?.reasonMetrics ?? []).length ===
                  0 ? (
                    <EmptyState />
                  ) : (
                    <div className="space-y-4">
                      {(data?.reasonMetrics ?? []).map(
                        (reason) => (
                          <div
                            key={reason.reason}
                            className="rounded-lg border p-4"
                          >
                            <div className="mb-2">
                              <span
                                className={`rounded-full px-2 py-1 text-xs font-medium ${
                                  reason.reason ===
                                  "FAILOVER"
                                    ? "bg-orange-100 text-orange-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {reason.reason}
                              </span>
                            </div>

                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Transações:
                                </span>
                                <span className="font-semibold">
                                  {reason.totalTransactions}
                                </span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Porcentagem:
                                </span>
                                <span className="font-semibold text-blue-600">
                                  {reason.percentage.toFixed(
                                    2
                                  )}
                                  %
                                </span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Aprovadas:
                                </span>
                                <span className="font-semibold text-green-600">
                                  {
                                    reason.approvedTransactions
                                  }
                                </span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Taxa Aprovação:
                                </span>
                                <span className="font-semibold text-green-600">
                                  {reason.approvalRate.toFixed(
                                    1
                                  )}
                                  %
                                </span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Volume:
                                </span>
                                <span className="font-semibold text-purple-600">
                                  R${" "}
                                  {reason.totalVolume.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Métricas de Sucesso dos Processadores */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="rounded-full bg-primary/10 p-2">
                      <Award className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">
                      Sucesso dos Processadores
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {(data?.processorSuccessMetrics ?? [])
                    .length === 0 ? (
                    <EmptyState />
                  ) : (
                    <div className="space-y-4">
                      {(
                        data?.processorSuccessMetrics ?? []
                      ).map((processor) => (
                        <div
                          key={processor.processor}
                          className="rounded-lg border p-6"
                        >
                          <div className="mb-4">
                            <h3 className="text-lg font-bold text-gray-800">
                              {processor.processor}
                            </h3>
                          </div>

                          <div className="grid grid-cols-2 gap-2 @sm:gap-4">
                            <div className="text-center">
                              <p className="text-lg font-bold text-blue-600 @sm:text-2xl">
                                {
                                  processor.totalTransactions
                                }
                              </p>
                              <p className="text-sm text-gray-600">
                                Total Transações
                              </p>
                            </div>

                            <div className="text-center">
                              <p className="text-lg font-bold text-green-600 @sm:text-2xl">
                                {processor.approvalRate.toFixed(
                                  1
                                )}
                                %
                              </p>
                              <p className="text-sm text-gray-600">
                                Taxa de Aprovação
                              </p>
                            </div>

                            <div className="text-center">
                              <p className="text-lg font-bold text-green-500 @sm:text-2xl">
                                {
                                  processor.approvedTransactions
                                }
                              </p>
                              <p className="text-sm text-gray-600">
                                Aprovadas
                              </p>
                            </div>

                            <div className="text-center">
                              <p className="text-lg font-bold text-red-500 @sm:text-2xl">
                                {
                                  processor.notApprovedTransactions
                                }
                              </p>
                              <p className="text-sm text-gray-600">
                                Não Aprovadas
                              </p>
                            </div>
                          </div>

                          {/* Barra de progresso para taxa de aprovação */}
                          <div className="mt-4">
                            <div className="mb-1 flex justify-between text-sm">
                              <span>Taxa de Aprovação</span>
                              <span>
                                {processor.approvalRate.toFixed(
                                  1
                                )}
                                %
                              </span>
                            </div>
                            <div className="h-2.5 w-full rounded-full bg-gray-200">
                              <div
                                className="h-2.5 rounded-full bg-green-600 transition-all duration-300"
                                style={{
                                  width: `${processor.approvalRate}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* White Labels */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-primary/10 p-2">
                    <CreditCard className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">
                    Top White Labels
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {(data?.whiteLabelMetrics ?? []).length ===
                0 ? (
                  <EmptyState />
                ) : (
                  <div className="space-y-4">
                    {(data?.whiteLabelMetrics ?? [])
                      .slice(0, 5)
                      .map((whiteLabel, index) => (
                        <div
                          key={whiteLabel.whiteLabelName}
                          className="rounded-lg border p-6 transition-shadow hover:shadow-md"
                        >
                          <div className="mb-4 flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-800">
                                #{index + 1}
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-800">
                                  {whiteLabel.whiteLabelName.startsWith(
                                    "WHL-"
                                  )
                                    ? `White Label ${whiteLabel.whiteLabelName.slice(4, 12)}...`
                                    : whiteLabel.whiteLabelName}
                                </h3>
                                <p className="text-sm text-gray-500">
                                  {
                                    whiteLabel.approvedTransactions
                                  }{" "}
                                  transações processadas
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-3 @xs:grid-cols-3">
                            <div className="rounded-lg bg-green-50 p-3 text-center">
                              <p className="text-lg font-bold text-green-600 @xs:text-xl">
                                R${" "}
                                {whiteLabel.volume.toLocaleString()}
                              </p>
                              <p className="text-xs text-green-700">
                                Volume Total
                              </p>
                            </div>

                            <div className="rounded-lg bg-blue-50 p-3 text-center">
                              <p className="text-lg font-bold text-blue-600 @xs:text-xl">
                                {
                                  whiteLabel.approvedTransactions
                                }
                              </p>
                              <p className="text-xs text-blue-700">
                                Aprovadas
                              </p>
                            </div>

                            <div className="rounded-lg bg-orange-50 p-3 text-center">
                              <p className="break-words text-lg font-bold text-orange-600 @xs:text-xl">
                                {whiteLabel.failoverRate.toFixed(
                                  1
                                )}
                                %
                              </p>
                              <p className="text-xs text-orange-700">
                                Failover
                              </p>
                            </div>
                          </div>

                          {/* Barra de progresso para volume relativo */}
                          <div className="mt-4">
                            <div className="mb-2 flex justify-between text-sm">
                              <span className="text-gray-600">
                                Participação no Volume Total
                              </span>
                              <span className="font-medium">
                                {data?.totalVolume
                                  ? (
                                      (whiteLabel.volume /
                                        data.totalVolume) *
                                      100
                                    ).toFixed(1)
                                  : "0.0"}
                                %
                              </span>
                            </div>
                            <div className="h-2 w-full rounded-full bg-gray-200">
                              <div
                                className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
                                style={{
                                  width: `${data?.totalVolume ? (whiteLabel.volume / data.totalVolume) * 100 : 0}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </section>
    </DashboardContainer>
  )
}
